import { ChevronLeft, ChevronRight, Copy, ArrowUp, ArrowDown, Search, Download, LoaderCircle } from "lucide-react";
import { Transaction } from "@shared/schema";
import { formatBitcoin, formatDate } from "@/lib/utils";
import { useState } from "react";
import { Link, useSearch } from "wouter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TransactionTableProps {
  transactions: Transaction[];
  // Add a new prop to disable internal pagination when external pagination is used
  disableInternalPagination?: boolean;
  // Callback to notify parent component (e.g. WalletSummary) when the filter type changes
  onFilterTypeChange?: (filter: "all" | "received" | "sent") => void;
  // Export CSV functionality
  onExportCsv?: () => void;
  isExporting?: boolean;
}

export default function TransactionTable({ 
  transactions, 
  disableInternalPagination = false,
  onFilterTypeChange,
  onExportCsv,
  isExporting = false
}: TransactionTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<"all" | "received" | "sent">("all");
  const itemsPerPage = 25;
  const search = useSearch();
  
  // Filter transactions based on search query and filter type
  const filteredTransactions = transactions.filter(tx => {
    // Apply transaction ID search filter
    const matchesSearch = searchQuery ? 
      tx.txid.toLowerCase().includes(searchQuery.toLowerCase()) : 
      true;
    
    // Apply transaction type filter (will be used in future)
    let matchesType = true;
    if (filterType !== "all") {
      if (filterType === "received" && !(tx.received_amount && tx.received_amount > 0)) {
        matchesType = false;
      }
      if (filterType === "sent" && !(tx.sent_amount && tx.sent_amount < 0)) {
        matchesType = false;
      }
    }
    
    return matchesSearch && matchesType;
  });
  
  // When internal pagination is disabled, show all transactions (don't slice)
  // This happens when external API-based pagination is in use
  let totalPages = 1;
  let startIndex = 0;
  let endIndex = itemsPerPage;
  let currentTransactions = filteredTransactions;
  
  // Only use internal pagination when not disabled
  if (!disableInternalPagination) {
    totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    startIndex = (currentPage - 1) * itemsPerPage;
    endIndex = Math.min(startIndex + itemsPerPage, filteredTransactions.length);
    currentTransactions = filteredTransactions.slice(startIndex, endIndex);
  }

  // Copy transaction ID to clipboard
  const copyToClipboard = (txid: string) => {
    navigator.clipboard.writeText(txid);
  };

  const handleFilterChange = (value: "all" | "received" | "sent") => {
    setFilterType(value);
    // Notify parent component if callback is provided
    if (onFilterTypeChange) {
      onFilterTypeChange(value);
    }
  };

  return (
    <TooltipProvider>
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Transaction History</h3>
        
        <div className="flex space-x-3 items-center">
          {onExportCsv && (
            <Button 
              variant="default"
              onClick={onExportCsv}
              disabled={isExporting}
              size="sm"
            >
              {isExporting ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" /> Export CSV
                </>
              )}
            </Button>
          )}
          
          <div className="relative">
            <Input
              type="text"
              placeholder="Search by transaction ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-full sm:w-60 border-gray-200 dark:border-gray-700 text-sm h-9 bg-white dark:bg-gray-800 dark:text-white"
            />
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>
          
          <Select value={filterType} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-[180px] border-gray-200 dark:border-gray-700 text-sm h-9 bg-white dark:bg-gray-800">
              <SelectValue placeholder="All Transactions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Transactions</SelectItem>
              <SelectItem value="received">Received Only</SelectItem>
              <SelectItem value="sent">Sent Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Timestamp (UTC)
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Transaction ID
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Amount Received
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Amount Sent
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Transaction Fee
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Balance
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
            {currentTransactions.map((tx) => (
              <tr 
                key={tx.txid} 
                className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {tx.timestamp ? new Date(tx.timestamp * 1000).toISOString().replace('T', ' ').substring(0, 19) : 'Unknown date'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono relative group">
                  <div className="flex items-center">
                    <Link 
                      href={`/search/transaction?query=${tx.txid}`} 
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline mr-2"
                    >
                      {tx.txid.substring(0, 8)}
                    </Link>
                    <button 
                      onClick={() => copyToClipboard(tx.txid)} 
                      className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title="Copy transaction ID"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {tx.received_amount && tx.received_amount > 0 ? (
                    <div className="flex items-center">
                      <span className="text-green-600 dark:text-green-400 font-medium">
                        +{formatBitcoin(tx.received_amount)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">0.00000000</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {tx.sent_amount && tx.sent_amount < 0 ? (
                    <div className="flex items-center">
                      <span className="text-red-600 dark:text-red-400 font-medium">
                        {formatBitcoin(tx.sent_amount)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">0.00000000</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {tx.fee === 0 && tx.received_amount && tx.received_amount > 0 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-gray-400 dark:text-gray-500 font-medium cursor-help">
                          {formatBitcoin(tx.fee)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Fee not charged to receiver</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {formatBitcoin(tx.fee)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {tx.balance !== undefined ? (
                    <span className={tx.balance >= 0 ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}>
                      {tx.balance < 0 ? "-" : ""}{formatBitcoin(Math.abs(tx.balance))}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">Not available</span>
                  )}
                </td>
              </tr>
            ))}
            
            {currentTransactions.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  <div className="flex flex-col items-center justify-center">
                    <Search className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm mb-1 font-medium">No transactions found</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Try adjusting your search criteria</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - exactly matching the design */}
      {!disableInternalPagination && totalPages > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          <Button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            variant="outline"
            size="sm"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => setCurrentPage(page)}
              size="sm"
              className={`flex items-center justify-center w-8 h-8 rounded ${
                currentPage === page
                  ? "bg-primary text-white"
                  : "border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {page}
            </Button>
          ))}
          
          <Button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            variant="outline"
            size="sm"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
